import { useEffect, useState, type ReactNode } from 'react'
import WidgetProvider, { type FormDataProps } from 'react-roast'
import { useOptionalUser } from '#app/utils/user.ts'

interface FeedbackWidgetProps {
	children: ReactNode
}

export function FeedbackWidget({ children }: FeedbackWidgetProps) {
	const user = useOptionalUser()
	const [isClient, setIsClient] = useState(false)

	// Only render React Roast on the client side
	useEffect(() => {
		setIsClient(true)
	}, [])

	// Force higher z-index during screenshot capture
	useEffect(() => {
		const style = document.createElement('style')
		style.innerHTML = `
      /* Force high z-index for common missing elements during screenshot capture */
      .react-roast-capturing nav,
      .react-roast-capturing header,
      .react-roast-capturing [role="banner"],
      .react-roast-capturing .navbar,
      .react-roast-capturing .header,
      .react-roast-capturing [data-testid*="nav"],
      .react-roast-capturing [class*="nav"],
      .react-roast-capturing [class*="header"],
      .react-roast-capturing .fixed,
      .react-roast-capturing .sticky,
      .react-roast-capturing [style*="position: fixed"],
      .react-roast-capturing [style*="position: sticky"],
      .react-roast-capturing [style*="position:fixed"],
      .react-roast-capturing [style*="position:sticky"] {
        z-index: 999999 !important;
        position: relative !important;
      }
    `
		document.head.appendChild(style)

		// Listen for React Roast screenshot capture events
		const handleCaptureStart = () => {
			document.documentElement.classList.add('react-roast-capturing')
		}

		const handleCaptureEnd = () => {
			document.documentElement.classList.remove('react-roast-capturing')
		}

		// Hook into html2canvas when React Roast loads it
		let originalHtml2Canvas: any = null

		// Wait for React Roast to load html2canvas, then override it
		const checkForHtml2Canvas = () => {
			if ((window as any).html2canvas && !originalHtml2Canvas) {
				originalHtml2Canvas = (window as any).html2canvas(
					window as any,
				).html2canvas = (element: any, options: any = {}) => {
					handleCaptureStart()

					// Enhanced html2canvas options for better capture
					const enhancedOptions = {
						...options,
						useCORS: true,
						allowTaint: true,
						scale: 1,
						logging: false,
						backgroundColor: '#ffffff',
						removeContainer: true,
						foreignObjectRendering: false,
						imageTimeout: 15000,
						onclone: (clonedDoc: Document) => {
							// Force visibility and z-index in cloned document
							const elementsToFix = clonedDoc.querySelectorAll(
								'nav, header, [role="banner"], .navbar, .header, [class*="nav"], [class*="header"], .fixed, .sticky',
							)
							elementsToFix.forEach((el: any) => {
								el.style.visibility = 'visible !important'
								el.style.opacity = '1 !important'
								el.style.zIndex = '999999 !important'
								el.style.position = 'relative !important'
							})

							if (options.onclone) {
								options.onclone(clonedDoc)
							}
						},
					}

					return originalHtml2Canvas(element, enhancedOptions).finally(() => {
						handleCaptureEnd()
					})
				}
			}
		}

		// Check periodically for html2canvas to be loaded
		const interval = setInterval(checkForHtml2Canvas, 100)

		// Clean up after 10 seconds
		const timeout = setTimeout(() => {
			clearInterval(interval)
		}, 10000)

		return () => {
			clearInterval(interval)
			clearTimeout(timeout)
			document.head.removeChild(style)
			if (originalHtml2Canvas) {
				;(window as any).html2canvas = originalHtml2Canvas
			}
		}
	}, [])

	const captureScreenshot = async (): Promise<string | undefined> => {
		try {
			// Try using the Screen Capture API if available
			if ('getDisplayMedia' in navigator.mediaDevices) {
				const stream = await navigator.mediaDevices.getDisplayMedia({
					// `mediaSource` is a legacy Firefox constraint not in the
					// standard MediaTrackConstraints type
					video: { mediaSource: 'screen' } as MediaTrackConstraints,
				})

				const video = document.createElement('video')
				video.srcObject = stream
				void video.play()

				return new Promise((resolve) => {
					video.addEventListener('loadedmetadata', () => {
						const canvas = document.createElement('canvas')
						canvas.width = video.videoWidth
						canvas.height = video.videoHeight

						const ctx = canvas.getContext('2d')
						ctx?.drawImage(video, 0, 0)

						stream.getTracks().forEach((track) => track.stop())

						const dataUrl = canvas.toDataURL('image/png')
						resolve(dataUrl)
					})
				})
			}
		} catch (error) {
			console.log('Screen capture not available or denied:', error)
		}

		return undefined
	}

	const handleSubmit = async ({ message, screenshotBlobs }: FormDataProps) => {
		try {
			let screenshotBase64: string | undefined = undefined

			// Try to use React Roast screenshot first
			if (Array.isArray(screenshotBlobs) && screenshotBlobs.length > 0) {
				const firstScreenshot = screenshotBlobs[0]

				if (
					firstScreenshot?.blob instanceof Blob &&
					firstScreenshot.blob.size > 0
				) {
					const reader = new FileReader()
					screenshotBase64 = await new Promise<string>((resolve) => {
						reader.onloadend = () => {
							resolve(reader.result as string)
						}
						reader.onerror = () => {
							resolve('')
						}
						reader.readAsDataURL(firstScreenshot.blob)
					})
				}
			} else {
				// Fallback to manual screen capture if no React Roast screenshot
				screenshotBase64 = await captureScreenshot()
			}

			// Send to our API route (which will save to database)
			const response = await fetch('/api/feedback', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					message,
					page: window.location.href,
					screenshot: screenshotBase64,
				}),
			})

			const result = (await response.json()) as {
				success?: boolean
				error?: string
			}

			if (!response.ok || !result.success) {
				console.error('Failed to send feedback:', result.error)
				return false
			}

			console.log('Feedback sent successfully!')
			return true
		} catch (error) {
			console.error('Error submitting feedback:', error)
			return false
		}
	}

	// Don't render React Roast on server side
	if (!isClient) {
		return <>{children}</>
	}

	return (
		<div data-debug="react-roast-container">
			<WidgetProvider
				mode="local"
				onFormSubmit={handleSubmit}
				customize={{
					form: {
						messageInput: {
							placeholder: 'Click an element first, then describe the issue...',
						},
						submitButton: {
							label: 'Send Feedback',
						},
						cancelButton: {
							label: 'Cancel',
						},
					},
					island: {
						placement: 'right-bottom',
						label: 'Feedback',
					},
				}}
			>
				{children}
			</WidgetProvider>
		</div>
	)
}
